import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../shared/Icon';
import PhotoCredit from '../shared/PhotoCredit';
import SeeAllLink from '../shared/SeeAllLink';
import { resizeForSurface } from '../../utils/imageSource';
import { formatDateRangeShort, formatDateShort, formatTime } from '../../utils/dateUtils';
import { inviteCover } from '../../utils/pendingInvites';
import { inviteTitle } from '../../utils/inviteTitle';
import { tripCover, RAIL_LIMIT } from '../../utils/homePlans';
import { daysUntil, PlanFact, StageChip } from './planCopy';

/**
 * The rail under the hero: every other plan, one chip each — a trip with its
 * stage and the one fact that matters at that stage, an invitation waiting
 * for an answer, a day out this week. Sorted by buildPlans (homePlans.js):
 * happening, invites, dated by date, ideas by last touch, recap owed last.
 *
 * A phone scrolls the whole rail sideways with the next chip peeking in; a
 * desktop shows RAIL_LIMIT chips and "All N plans" for the rest. The Planner
 * stays the full list.
 */
function RailChip({ entry, onOpenInvite }) {
  const { t } = useTranslation('home');
  const { kind, item } = entry;

  let photo = null;
  let name = '';
  let fact = null;
  let to = null;
  let onClick;
  let chip = null;
  let highlight = false;

  if (kind === 'trip') {
    photo = tripCover(item);
    name = item.name;
    fact = <PlanFact trip={item} stage={entry.stage} fact={entry.fact} />;
    to = `/trip/${encodeURIComponent(item.id)}`;
    chip = <StageChip stage={entry.stage} daysUntil={daysUntil(item)} />;
  } else if (kind === 'invite') {
    photo = inviteCover(item);
    name = inviteTitle(item.title, { trip: item.type === 'trip' });
    const when = item.type === 'trip'
      ? (item.start_date && item.end_date ? formatDateRangeShort(item.start_date, item.end_date) : null)
      : (item.scheduled_date ? formatDateShort(item.scheduled_date) : null);
    fact = [when, t('rail.invitedBy', { name: item.from_user_name })].filter(Boolean).join(t('rail.separator'));
    onClick = () => onOpenInvite(item);
    chip = <span className="px-2 h-5 inline-flex items-center rounded-full bg-coral-light text-coral-dark text-[11px] font-bold">{t('rail.reply')}</span>;
    highlight = true;
  } else {
    photo = item.image_url || item.imageUrl || item.cover_photo || item.custom_image || null;
    name = item.title || item.name || item.custom_title || t('rail.anActivity');
    const date = item.scheduled_date || item.scheduledDate;
    const time = item.scheduled_time || item.scheduledTime;
    fact = [date ? formatDateShort(date) : null, time ? formatTime(time) : null, item.location || item.custom_location]
      .filter(Boolean).join(t('rail.separator'));
    to = '/activity-planner';
    chip = <span className="px-2 h-5 inline-flex items-center rounded-full bg-surface-secondary text-content-secondary text-[11px] font-semibold">{t('rail.dayOut')}</span>;
  }

  const className = `flex items-center gap-3 min-w-0 flex-shrink-0 w-[260px] md:w-auto md:flex-1 snap-start p-2 pr-3 bg-surface rounded-xl border ${highlight ? 'border-coral' : 'border-border'} can-hover:hover:border-content-muted active:scale-[0.99] transition-colors duration-fast text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light`;
  const body = (
    <>
      <div className="relative w-11 h-11 rounded-lg overflow-hidden bg-background flex-shrink-0">
        {photo ? (
          <>
            <img src={resizeForSurface(photo, 'thumb')} alt="" className="w-full h-full object-cover" loading="lazy" />
            {/* The chip IS the link/button: the credit can't be a second control inside it. */}
            <PhotoCredit src={resizeForSurface(photo, 'thumb')} size="xs" interactive={false} />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={kind === 'activity' ? 'calendar' : 'suitcase'} className="w-5 h-5 text-content-muted" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-content-primary truncate">{name}</p>
        <p className="text-xs text-content-secondary truncate">{fact}</p>
      </div>
      <div className="flex-shrink-0">{chip}</div>
    </>
  );

  if (to) return <Link to={to} className={className}>{body}</Link>;
  return <button type="button" onClick={onClick} className={className}>{body}</button>;
}

export default function PlansRail({ rail, totalPlans, onOpenInvite }) {
  const { t } = useTranslation('home');
  if (!rail || rail.length === 0) return null;
  const overflow = rail.length > RAIL_LIMIT;

  return (
    <section aria-label={t('rail.ariaLabel')} className="-mt-2">
      <div className="flex items-center justify-between md:hidden mb-2">
        <p className="text-sm font-semibold text-content-secondary">{t('rail.title')}</p>
        <SeeAllLink to="/activity-planner">{t('rail.allPlans', { count: totalPlans })}</SeeAllLink>
      </div>
      <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-4 px-4 pb-2 md:mx-0 md:px-0 md:pb-0 md:overflow-visible">
        {rail.map((entry, index) => (
          <div key={entry.key} className={`contents ${index >= RAIL_LIMIT ? 'md:hidden' : ''}`}>
            <RailChip entry={entry} onOpenInvite={onOpenInvite} />
          </div>
        ))}
        <div className="hidden md:block flex-shrink-0">
          <SeeAllLink to="/activity-planner">
            {overflow ? t('rail.morePlans', { count: rail.length - RAIL_LIMIT }) : t('rail.allPlans', { count: totalPlans })}
          </SeeAllLink>
        </div>
      </div>
    </section>
  );
}
